import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { AuthProvider } from '../Providers/authProvider';
import { AuthenticatedUser } from '../Types/AuthenticatedUser';

interface AuthContextType {
    user: AuthenticatedUser | null;
    isLoggedIn: boolean;
    loading: boolean;
    refreshUser: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthenticatedUser | null>(null);
    const [loading, setLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        setLoading(true);
        try {
            const loggedInUser = await AuthProvider().GetLoggedInUser();
            setUser(loggedInUser);
        } catch (error) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        await AuthProvider().Logout();
        setUser(null);
    }, []);

    // Only fetch user on initial mount
    useEffect(() => {
        let isMounted = true;

        const fetchUser = async () => {
            setLoading(true);
            try {
                const loggedInUser = await AuthProvider().GetLoggedInUser();
                if (isMounted) {
                    setUser(loggedInUser);
                }
            } catch (error) {
                if (isMounted) {
                    setUser(null);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchUser();

        return () => {
            isMounted = false;
        };
    }, []);

    const value = useMemo(
        () => ({
            user,
            isLoggedIn: !!user,
            loading,
            refreshUser,
            logout
        }),
        [user, loading, refreshUser, logout]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthContextProvider');
    }
    return context;
};
